import { useMemo } from 'react';
import { Formik } from 'formik';
import type { FormikHelpers } from 'formik';

import { getFragmentData } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { Loading } from '../../components/Loading';
import { useFindAllDocumentTemplatesQuery } from '../../pages/DocumentAndSectionTemplates/hooks/useFindAllDocumentTemplatesQuery.hook';
import type { MissionReportWithItsInfo } from '../../pages/MissionReport/types/mission-report-with-its-info.type';

import type { FormVariant } from '../enums/variant.enum';
import { MissionReportInfoStateForm_ResolvableMissionType_Fragment } from '../MissionReportInfoState';

import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { getMissionReportInfoSchema } from './schemas/mission-report-info.schema';
import { MissionReportInfoFormFields } from './MissionReportInfoFormFields';

interface Props {
  readonly formVariant: FormVariant;
  readonly initialValues?: MissionReportWithItsInfo;
  readonly onCancel?: () => void;
  readonly onSuccess?: () => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly submitLabel?: string;
  readonly useSubmitFactory: (
    handleSuccess?: () => void,
  ) => (
    reportWithItsInfo: MissionReportWithItsInfo,
    formikHelpers: FormikHelpers<MissionReportWithItsInfo>,
  ) => void;
}

export const MissionReportInfoForm = ({
  formVariant,
  initialValues: pInitialValues,
  onCancel: handleCancel,
  onSuccess: handleSuccess,
  setMustBlockAction,
  showCancelButton,
  submitLabel = 'Valider',
  useSubmitFactory,
}: Readonly<Props>) => {
  const { genericStatusesCodeMap, reportWorkflows } = useReferencesContext();

  const { resolvableMissionType } = useMissionReadyContext();

  const { data, isSuccess } = useFindAllDocumentTemplatesQuery();

  const { initialValues } = useInitialValuesFactory(pInitialValues);

  const allDocumentTemplates = data?.findAllDocumentTemplates;

  const reportTemplates =
    allDocumentTemplates?.filter(
      (template) =>
        (template.statusId === genericStatusesCodeMap.Enabled._id ||
          template._id === initialValues.templateId) &&
        template.document.typeId === initialValues.typeId,
    ) ?? EMPTY_ARRAY;

  const reportTemplateIds = getFragmentData(
    MissionReportInfoStateForm_ResolvableMissionType_Fragment,
    resolvableMissionType,
  )?.reportTemplateIds;

  const suggestedTemplateIds = useMemo(
    () => new Set<Id>(reportTemplateIds ?? EMPTY_ARRAY),
    [reportTemplateIds],
  );

  const validationSchema = useMemo(
    () => getMissionReportInfoSchema(formVariant, reportWorkflows),
    [formVariant, reportWorkflows],
  );

  return (
    <>
      {!isSuccess && <Loading />}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={useSubmitFactory(handleSuccess)}
        validationSchema={validationSchema}
      >
        <MissionReportInfoFormFields
          formVariant={formVariant}
          onCancel={handleCancel}
          reportTemplates={reportTemplates}
          setMustBlockAction={setMustBlockAction}
          showCancelButton={showCancelButton}
          submitLabel={submitLabel}
          suggestedTemplateIds={suggestedTemplateIds}
        />
      </Formik>
    </>
  );
};
